// FR-RD14 (desktop/mobile resume): a `LocalSource` over disk.rs's segments plus the
// upload.json sidecar (recording_read_upload_state / recording_write_upload_state), so the
// same `runRecovery` state machine that drives web tab-crash recovery also drives a crashed
// desktop recording or a mobile-shell recording that was stopped while offline.
import {
  finishDiskSession,
  listRecoverableSessions,
  readUploadState,
  replayRecoverable,
  writeUploadState,
  type RecoverableSession,
} from "../../desktop";
import { isSessionActive, sessionsLockedByAnyTab } from "./activeSessions";
import type { LocalSource, PersistedUploadState } from "./types";

function parseState(json: string | null): PersistedUploadState | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as PersistedUploadState;
  } catch {
    return null;
  }
}

// disk.rs's `list_recoverable` already sorts by id (UUIDv7, so lexical order is creation
// order) — recovery just needs the ids. Cached per `list()` call so `readChunks` (which needs
// `last_seq` to know how many segments to replay) doesn't have to re-list.
let sessionsById = new Map<string, RecoverableSession>();

export const diskLocalSource: LocalSource = {
  async list() {
    const sessions = await listRecoverableSessions();
    sessionsById = new Map(sessions.map((s) => [s.id, s]));
    // A session this same process is actively recording right now is not this sweep's to
    // touch — see activeSessions.ts.
    const locked = await sessionsLockedByAnyTab();
    return sessions.filter((s) => !isSessionActive(s.id) && !locked.has(s.id)).map((s) => s.id);
  },

  async readChunks(id) {
    const session = sessionsById.get(id);
    if (!session) throw new Error(`recoverable session ${id} disappeared from disk`);
    if (session.last_seq === 0) throw new Error(`session ${id} has no segments on disk`);
    return replayRecoverable(session);
  },

  async loadState(id) {
    // RecorderPage writes the sidecar immediately after starting a disk session (before any
    // segment is appended), so `null` here means a crash landed in that narrow window before
    // the first write — there is no orgId to resume into (disk.rs's manifest never stores
    // one). Treated as "nothing to resume yet": the segments stay on disk, untouched, rather
    // than guessing at an org. See F5_NOTES.md for why this is an accepted, narrow gap.
    return parseState(await readUploadState(id));
  },

  async saveState(id, state) {
    await writeUploadState(id, JSON.stringify(state));
  },

  async markFailed(id, err) {
    const current = parseState(await readUploadState(id));
    if (!current) return;
    const failed: PersistedUploadState = { ...current, status: "failed", lastError: err, updatedAt: Date.now() };
    await writeUploadState(id, JSON.stringify(failed));
  },

  async deleteLocal(id) {
    // finish_session (disk.rs) marks the manifest Finished, which drops it out of
    // list_recoverable (FR-RD14) — see F5_NOTES.md for why this crate doesn't also reclaim
    // the directory's disk space here (a pre-existing property of finish_session, not changed
    // by this PR).
    await finishDiskSession(id);
  },
};
